import React, { useEffect, useState } from 'react';
import { clearCustomerSession } from './kioskCustomerSession';
import { useNavigate } from 'react-router-dom';
import { API_URL, buildKioskAuthHeaders, getDefaultBrandLogoUrl, resolveMediaUrl } from './api';
import { isOrgasmeatExperience, OrgasmeatAttract } from './OrgasmeatExperience';

const DEFAULT_SETTINGS = {
  attractMessage: 'CLIQUER POUR COMMANDER',
  attractTextColor: '#fff8ef',
  attractFontSize: 72,
  attractBackgroundType: 'image',
  attractBackgroundMedia: '',
  displayLogoMedia: '',
  closedAttractBackgroundType: 'image',
  closedAttractBackgroundMedia: '',
  openingTime: '',
  closingTime: '',
  breakStartTime: '',
  breakEndTime: '',
  weeklySchedule: null,
  closedAttractMessage: 'Le restaurant est actuellement fermé.',
};
const DAYS = [
  { key: 'sunday', label: 'dimanche' },
  { key: 'monday', label: 'lundi' },
  { key: 'tuesday', label: 'mardi' },
  { key: 'wednesday', label: 'mercredi' },
  { key: 'thursday', label: 'jeudi' },
  { key: 'friday', label: 'vendredi' },
  { key: 'saturday', label: 'samedi' },
];
const FALLBACK_BRAND_LOGO_URL = getDefaultBrandLogoUrl({
  width: 420,
  height: 420,
  quality: 62,
  fit: 'contain',
});

function parseTimeToMinutes(value) {
  const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
}

function formatTime(minutes) {
  const hours = String(Math.floor(minutes / 60)).padStart(2, '0');
  const mins = String(minutes % 60).padStart(2, '0');
  return `${hours}:${mins}`;
}

function normalizeWeeklySchedule(rawSchedule, fallback = {}) {
  let parsed = rawSchedule;
  if (typeof rawSchedule === 'string') {
    const trimmed = rawSchedule.trim();
    if (!trimmed) {
      parsed = null;
    } else {
      try {
        parsed = JSON.parse(trimmed);
      } catch (error) {
        parsed = null;
      }
    }
  }

  const fallbackDay = {
    enabled: true,
    openingTime: String(fallback.openingTime || '').trim(),
    closingTime: String(fallback.closingTime || '').trim(),
    breakStartTime: String(fallback.breakStartTime || '').trim(),
    breakEndTime: String(fallback.breakEndTime || '').trim(),
  };

  return DAYS.reduce((acc, day) => {
    const dayRaw = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed[day.key] : null;
    acc[day.key] = {
      enabled: dayRaw?.enabled !== undefined ? !!dayRaw.enabled : true,
      openingTime: String(dayRaw?.openingTime ?? fallbackDay.openingTime).trim(),
      closingTime: String(dayRaw?.closingTime ?? fallbackDay.closingTime).trim(),
      breakStartTime: String(dayRaw?.breakStartTime ?? fallbackDay.breakStartTime).trim(),
      breakEndTime: String(dayRaw?.breakEndTime ?? fallbackDay.breakEndTime).trim(),
    };
    return acc;
  }, {});
}

function getNextOpeningLabel(schedule, currentDayIndex) {
  for (let offset = 1; offset <= 7; offset += 1) {
    const dayIndex = (currentDayIndex + offset) % 7;
    const day = DAYS[dayIndex];
    const daySchedule = schedule[day.key];
    if (!daySchedule || daySchedule.enabled === false) {
      continue;
    }
    const openingMinutes = parseTimeToMinutes(daySchedule.openingTime);
    const closingMinutes = parseTimeToMinutes(daySchedule.closingTime);
    if (openingMinutes !== null && closingMinutes !== null && closingMinutes > openingMinutes) {
      return offset === 1
        ? `Repassez demain à ${formatTime(openingMinutes)}`
        : `Repassez ${day.label} à ${formatTime(openingMinutes)}`;
    }
  }
  return '';
}

function getOpeningStatus(settings, referenceDate = Date.now()) {
  const schedule = normalizeWeeklySchedule(settings.weeklySchedule, settings);
  const now = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  const currentDayIndex = now.getDay();
  const day = DAYS[currentDayIndex];
  const daySchedule = schedule[day.key];

  if (!daySchedule || daySchedule.enabled === false) {
    return {
      isOpen: false,
      reopeningLabel: getNextOpeningLabel(schedule, currentDayIndex),
    };
  }

  const openingMinutes = parseTimeToMinutes(daySchedule.openingTime);
  const closingMinutes = parseTimeToMinutes(daySchedule.closingTime);
  const breakStartMinutes = parseTimeToMinutes(daySchedule.breakStartTime);
  const breakEndMinutes = parseTimeToMinutes(daySchedule.breakEndTime);

  if (openingMinutes === null || closingMinutes === null || closingMinutes <= openingMinutes) {
    return { isOpen: true, reopeningLabel: '' };
  }

  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const hasBreak =
    breakStartMinutes !== null &&
    breakEndMinutes !== null &&
    breakStartMinutes > openingMinutes &&
    breakEndMinutes > breakStartMinutes &&
    breakEndMinutes < closingMinutes;

  if (currentMinutes < openingMinutes) {
    return {
      isOpen: false,
      reopeningLabel: `Repassez à ${formatTime(openingMinutes)}`,
    };
  }
  if (hasBreak && currentMinutes >= breakStartMinutes && currentMinutes < breakEndMinutes) {
    return {
      isOpen: false,
      reopeningLabel: `Repassez à ${formatTime(breakEndMinutes)}`,
    };
  }
  if (currentMinutes >= closingMinutes) {
    return {
      isOpen: false,
      reopeningLabel: getNextOpeningLabel(schedule, currentDayIndex),
    };
  }
  return { isOpen: true, reopeningLabel: '' };
}

function AttractScreen() {
  useEffect(() => { clearCustomerSession(); }, []);
  const navigate = useNavigate();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const orgasmeat = isOrgasmeatExperience(settings);
  const [currentTime, setCurrentTime] = useState(() => Date.now());

  useEffect(() => {
    let active = true;
    let refreshInterval = null;

    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/printer-settings`, {
          headers: buildKioskAuthHeaders(),
        });
        if (!response.ok) return;
        const data = await response.json();
        if (!active) {
          return;
        }
        setSettings((prev) => ({
          ...prev,
          ...data,
          attractBackgroundMedia: resolveMediaUrl(data.attractBackgroundMedia),
          displayLogoMedia: resolveMediaUrl(data.displayLogoMedia) || FALLBACK_BRAND_LOGO_URL,
          closedAttractBackgroundMedia: resolveMediaUrl(data.closedAttractBackgroundMedia),
        }));
        setSettingsLoaded(true);
      } catch (error) {
        console.error('Erreur chargement écran de veille:', error);
      }
    };

    fetchSettings();
    refreshInterval = window.setInterval(fetchSettings, 15000);

    return () => {
      active = false;
      if (refreshInterval) {
        window.clearInterval(refreshInterval);
      }
    };
  }, []);

  useEffect(() => {
    const statusInterval = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 15000);

    return () => {
      window.clearInterval(statusInterval);
    };
  }, []);

  useEffect(() => {
    if (!settingsLoaded || orgasmeat) return undefined;
    const existingScript = document.querySelector('script[data-dotlottie-player="true"]');
    if (existingScript) {
      return undefined;
    }

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/@dotlottie/player-component@2.7.12/dist/dotlottie-player.mjs';
    script.type = 'module';
    script.dataset.dotlottiePlayer = 'true';
    document.head.appendChild(script);

    return undefined;
  }, [settingsLoaded, orgasmeat]);

  const openingStatus = getOpeningStatus(settings, currentTime);
  const displayedMessage = openingStatus.isOpen
    ? (settings.attractMessage || DEFAULT_SETTINGS.attractMessage)
    : (settings.closedAttractMessage || DEFAULT_SETTINGS.closedAttractMessage);
  if (orgasmeat) return <OrgasmeatAttract isOpen={openingStatus.isOpen} message={displayedMessage} reopeningLabel={openingStatus.reopeningLabel} onStart={() => navigate('/welcome')} />;
  return (
    <button
      type="button"
      className="attract-screen"
      onClick={() => navigate('/welcome')}
    >
      <div className="attract-arrow-wrap" aria-hidden="true">
        <dotlottie-player
          class="attract-arrow"
          src="https://lottie.host/ff6358b8-aff4-4259-ab20-26f737bae78d/I2FhmDOyc3.lottie"
          background="transparent"
          speed="1"
          loop
          autoplay
        />
      </div>
      <div className="attract-brand-logo" aria-hidden="true">
        <img src={settings.displayLogoMedia || FALLBACK_BRAND_LOGO_URL} alt="" />
      </div>
      <div
        className="attract-message"
        style={{
          color: settings.attractTextColor || DEFAULT_SETTINGS.attractTextColor,
          fontSize: `${settings.attractFontSize || DEFAULT_SETTINGS.attractFontSize}px`,
        }}
      >
        <span>{displayedMessage}</span>
        {!openingStatus.isOpen && openingStatus.reopeningLabel && (
          <small className="attract-reopening">{openingStatus.reopeningLabel}</small>
        )}
      </div>
    </button>
  );
}

export default AttractScreen;
